import { formatRgba, hexToRgba, isCssVar } from "./rgba";
import type { Gradient, GradientStop } from "./types";

/**
 * Serialize a Gradient to its canonical string form.
 *
 * Canonical rules:
 * - Stops are sorted ascending by position before emitting (original array not mutated).
 * - Each stop emitted as `rgba(R, G, B, A) P%` where:
 *     - R, G, B are integers 0..255
 *     - A has at most 2 decimal places, trailing zeros trimmed (via formatRgba)
 *     - P is an integer 0..100 (rounded via Math.round)
 * - Linear:  `linear-gradient(<angle>deg, <stop1>, <stop2>, ...)` — angle is
 *   rounded to integer and falls back to 0 for non-finite input.
 * - Radial:  `radial-gradient(circle at center, <stop1>, <stop2>, ...)`
 * - A single space after each comma; no extra whitespace.
 *
 * Lossy contract — round-trip is bitwise-exact only for canonical values:
 * - angle: rounded to integer on emit.
 * - opacity: emitted with ≤2 decimals (values beyond 2 decimals are truncated).
 * - position: rounded to integer.
 * Callers storing Gradient objects should normalize these fields at creation
 * time if exact round-trip is required for non-integer inputs.
 */
export const serializeGradient = (g: Gradient): string => {
	switch (g.type) {
		case "linear": {
			const sorted = [...g.stops].sort((a, b) => a.position - b.position);
			const tokens = sorted.map(formatStop).join(", ");
			const angle = Number.isFinite(g.angle) ? Math.round(g.angle) : 0;
			return `linear-gradient(${angle}deg, ${tokens})`;
		}
		case "radial": {
			const sorted = [...g.stops].sort((a, b) => a.position - b.position);
			const tokens = sorted.map(formatStop).join(", ");
			return `radial-gradient(circle at center, ${tokens})`;
		}
		default: {
			// Exhaustive switch — new Gradient variants must be handled here.
			const _exhaustive: never = g;
			throw new Error(
				`serializeGradient: unknown gradient type: ${JSON.stringify(_exhaustive)}`,
			);
		}
	}
};

const formatStop = (stop: GradientStop): string => {
	const position = Math.round(stop.position);

	// When the stop color is a CSS variable reference, emit it directly so the
	// gradient updates live when design-system theme colors change.
	if (isCssVar(stop.color)) {
		return `${stop.color} ${position}%`;
	}

	const { r, g, b } = hexToRgba(stop.color);
	const color = formatRgba(r, g, b, stop.opacity);
	return `${color} ${position}%`;
};
